import React, { useState } from 'react';
import {
  Box,
  Image,
  HStack,
  VStack,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Flex,
  Text,
  Spinner,
} from '@chakra-ui/react';
import { FaChevronLeft, FaChevronRight, FaExpand } from 'react-icons/fa';

interface ImageGalleryProps {
  images: string[];
  alt?: string;
  maxHeight?: string;
}

const ImageGallery: React.FC<ImageGalleryProps> = ({ 
  images, 
  alt = "Product image",
  maxHeight = "400px"
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadingImages, setLoadingImages] = useState<Set<number>>(new Set());
  const { isOpen, onOpen, onClose } = useDisclosure();

  if (!images || images.length === 0) {
    return (
      <Box
        w="full"
        h={maxHeight}
        bg="gray.100"
        display="flex"
        alignItems="center"
        justifyContent="center"
        borderRadius="md"
      >
        <Text color="gray.500">No images available</Text>
      </Box>
    );
  }

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const goToImage = (index: number) => {
    setCurrentIndex(index);
  };

  const handleImageLoad = (index: number) => {
    setLoadingImages(prev => {
      const newSet = new Set(prev);
      newSet.delete(index);
      return newSet;
    });
  };

  const handleImageLoadStart = (index: number) => {
    setLoadingImages(prev => new Set(prev).add(index));
  };

  return (
    <VStack spacing={4} w="full">
      {/* Main Image Display */}
      <Box
        position="relative"
        w="full"
        h={maxHeight}
        borderRadius="lg"
        overflow="hidden"
        bg="gray.100"
        cursor="pointer"
        onClick={onOpen}
        _hover={{ transform: 'scale(1.02)' }}
        transition="transform 0.2s"
      >
        {loadingImages.has(currentIndex) && (
          <Flex
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            zIndex={2}
          >
            <Spinner size="lg" color="brand.primary" />
          </Flex>
        )}
        
        <Image
          src={images[currentIndex]}
          alt={`${alt} ${currentIndex + 1}`}
          w="full"
          h="full"
          objectFit="cover"
          onLoad={() => handleImageLoad(currentIndex)}
          onLoadStart={() => handleImageLoadStart(currentIndex)}
        />

        {/* Navigation Arrows */}
        {images.length > 1 && (
          <>
            <IconButton
              icon={<FaChevronLeft />}
              aria-label="Previous image"
              position="absolute"
              left={2}
              top="50%"
              transform="translateY(-50%)"
              size="md"
              bg="blackAlpha.600"
              color="white"
              _hover={{ bg: 'blackAlpha.800' }}
              onClick={(e) => {
                e.stopPropagation();
                goToPrevious();
              }}
            />
            <IconButton
              icon={<FaChevronRight />}
              aria-label="Next image"
              position="absolute"
              right={2}
              top="50%"
              transform="translateY(-50%)"
              size="md"
              bg="blackAlpha.600"
              color="white"
              _hover={{ bg: 'blackAlpha.800' }}
              onClick={(e) => {
                e.stopPropagation();
                goToNext();
              }}
            />
          </>
        )}

        {/* Expand Button */}
        <IconButton
          icon={<FaExpand />}
          aria-label="View fullscreen"
          position="absolute"
          top={2}
          right={2}
          size="sm"
          bg="blackAlpha.600"
          color="white"
          _hover={{ bg: 'blackAlpha.800' }}
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
        />

        {/* Image Counter */}
        {images.length > 1 && (
          <Box
            position="absolute"
            bottom={2}
            right={2}
            bg="blackAlpha.700"
            color="white"
            px={2}
            py={1}
            borderRadius="md"
            fontSize="sm"
          >
            {currentIndex + 1} / {images.length}
          </Box>
        )}
      </Box>

      {/* Thumbnail Navigation */}
      {images.length > 1 && (
        <HStack spacing={2} w="full" justify="center" flexWrap="wrap">
          {images.map((image, index) => (
            <Box
              key={index}
              position="relative"
              cursor="pointer"
              onClick={() => goToImage(index)}
              borderRadius="md"
              overflow="hidden"
              border="2px solid"
              borderColor={index === currentIndex ? 'brand.primary' : 'transparent'}
              _hover={{ borderColor: 'brand.primaryHover' }}
              transition="border-color 0.2s"
            >
              <Image
                src={image}
                alt={`${alt} thumbnail ${index + 1}`}
                w="60px"
                h="60px"
                objectFit="cover"
                opacity={index === currentIndex ? 1 : 0.7}
                _hover={{ opacity: 1 }}
                transition="opacity 0.2s"
              />
            </Box>
          ))}
        </HStack>
      )}

      {/* Fullscreen Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="full">
        <ModalOverlay bg="blackAlpha.900" />
        <ModalContent bg="transparent">
          <ModalCloseButton color="white" size="lg" top={4} right={4} zIndex={10} />
          <ModalBody p={0} display="flex" alignItems="center" justifyContent="center">
            <Box position="relative" maxW="90vw" maxH="90vh">
              <Image
                src={images[currentIndex]}
                alt={`${alt} ${currentIndex + 1}`}
                maxW="full"
                maxH="full"
                objectFit="contain"
              />

              {/* Fullscreen Navigation */}
              {images.length > 1 && (
                <>
                  <IconButton
                    icon={<FaChevronLeft />}
                    aria-label="Previous image"
                    position="absolute"
                    left={-16}
                    top="50%"
                    transform="translateY(-50%)"
                    size="lg"
                    bg="blackAlpha.600"
                    color="white"
                    _hover={{ bg: 'blackAlpha.800' }}
                    onClick={goToPrevious}
                  />
                  <IconButton
                    icon={<FaChevronRight />}
                    aria-label="Next image"
                    position="absolute"
                    right={-16}
                    top="50%"
                    transform="translateY(-50%)"
                    size="lg"
                    bg="blackAlpha.600"
                    color="white"
                    _hover={{ bg: 'blackAlpha.800' }}
                    onClick={goToNext}
                  />

                  {/* Fullscreen Thumbnails */}
                  <HStack
                    position="absolute"
                    bottom={-20}
                    left="50%"
                    transform="translateX(-50%)"
                    spacing={2}
                    bg="blackAlpha.600"
                    p={2}
                    borderRadius="md"
                  >
                    {images.map((image, index) => (
                      <Box
                        key={index}
                        cursor="pointer"
                        onClick={() => goToImage(index)}
                        borderRadius="sm"
                        overflow="hidden"
                        border="1px solid"
                        borderColor={index === currentIndex ? 'brand.primary' : 'transparent'}
                      >
                        <Image
                          src={image}
                          alt={`${alt} thumbnail ${index + 1}`}
                          w="40px"
                          h="40px"
                          objectFit="cover"
                          opacity={index === currentIndex ? 1 : 0.6}
                          _hover={{ opacity: 1 }}
                        />
                      </Box>
                    ))}
                  </HStack>
                </>
              )}
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default ImageGallery; 